import { DragDropContext, type OnDragEndResponder } from "@hello-pangea/dnd";
import isEqual from "lodash/isEqual";
import { useDataProvider, useListContext, type DataProvider } from "ra-core";
import { useEffect, useState } from "react";

import type { Job } from "../../providers/fakerest/dataGenerator/types";
import { JobColumn } from "./JobColumn";
import { JOB_STAGES, getJobsByStatus, type JobsByStatus } from "./jobStages";

export const JobKanban = () => {
  const { data: jobs, isPending, refetch, sort } = useListContext<Job>();
  const dataProvider = useDataProvider();

  const [jobsByStatus, setJobsByStatus] = useState<JobsByStatus>(
    getJobsByStatus([]),
  );

  useEffect(() => {
    if (jobs) {
      const newJobsByStatus = getJobsByStatus(jobs, sort);
      if (!isEqual(newJobsByStatus, jobsByStatus)) {
        setJobsByStatus(newJobsByStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, sort]);

  if (isPending) return null;

  const onDragEnd: OnDragEndResponder = (result) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceStatus = source.droppableId as Job["status"];
    const destinationStatus = destination.droppableId as Job["status"];
    const sourceJob = jobsByStatus[sourceStatus][source.index]!;
    const destinationJob = jobsByStatus[destinationStatus][
      destination.index
    ] ?? {
      status: destinationStatus,
      index: undefined,
    };

    // Optimistic local state update
    setJobsByStatus(
      updateJobStatusLocal(
        sourceJob,
        { status: sourceStatus, index: source.index },
        { status: destinationStatus, index: destination.index },
        jobsByStatus,
      ),
    );

    // Persist changes
    updateJobStatus(sourceJob, destinationJob, dataProvider).then(() => {
      refetch();
    });
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {JOB_STAGES.map((stage) => (
          <JobColumn
            key={stage.value}
            status={stage.value}
            jobs={jobsByStatus[stage.value]}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

const updateJobStatusLocal = (
  sourceJob: Job,
  source: { status: Job["status"]; index: number },
  destination: { status: Job["status"]; index?: number },
  jobsByStatus: JobsByStatus,
): JobsByStatus => {
  if (source.status === destination.status) {
    // Moving within same column
    const column = [...jobsByStatus[source.status]];
    column.splice(source.index, 1);
    column.splice(destination.index ?? column.length, 0, sourceJob);
    return {
      ...jobsByStatus,
      [source.status]: column,
    };
  } else {
    // Moving across columns
    const sourceColumn = [...jobsByStatus[source.status]];
    const destColumn = [...jobsByStatus[destination.status]];
    sourceColumn.splice(source.index, 1);
    destColumn.splice(destination.index ?? destColumn.length, 0, {
      ...sourceJob,
      status: destination.status,
    });
    return {
      ...jobsByStatus,
      [source.status]: sourceColumn,
      [destination.status]: destColumn,
    };
  }
};

const updateJobStatus = async (
  source: Job,
  destination: { status: Job["status"]; index?: number },
  dataProvider: DataProvider,
) => {
  if (source.status === destination.status) {
    // Moving within same column - update indices
    const { data: columnJobs } = await dataProvider.getList<Job>("jobs", {
      sort: { field: "index", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
      filter: { status: source.status },
    });
    const destinationIndex = destination.index ?? columnJobs.length;

    if (source.index > destinationIndex) {
      // Moved up
      await Promise.all([
        ...columnJobs
          .filter(
            (job) => job.index >= destinationIndex && job.index < source.index,
          )
          .map((job) =>
            dataProvider.update("jobs", {
              id: job.id,
              data: { index: job.index + 1 },
              previousData: job,
            }),
          ),
        dataProvider.update("jobs", {
          id: source.id,
          data: { index: destinationIndex },
          previousData: source,
        }),
      ]);
    } else {
      // Moved down
      await Promise.all([
        ...columnJobs
          .filter(
            (job) => job.index <= destinationIndex && job.index > source.index,
          )
          .map((job) =>
            dataProvider.update("jobs", {
              id: job.id,
              data: { index: job.index - 1 },
              previousData: job,
            }),
          ),
        dataProvider.update("jobs", {
          id: source.id,
          data: { index: destinationIndex },
          previousData: source,
        }),
      ]);
    }
  } else {
    // Moving across columns
    const [{ data: sourceJobs }, { data: destJobs }] = await Promise.all([
      dataProvider.getList<Job>("jobs", {
        sort: { field: "index", order: "ASC" },
        pagination: { page: 1, perPage: 100 },
        filter: { status: source.status },
      }),
      dataProvider.getList<Job>("jobs", {
        sort: { field: "index", order: "ASC" },
        pagination: { page: 1, perPage: 100 },
        filter: { status: destination.status },
      }),
    ]);
    const destinationIndex = destination.index ?? destJobs.length;

    await Promise.all([
      // Decrease indices in source column
      ...sourceJobs
        .filter((job) => job.index > source.index)
        .map((job) =>
          dataProvider.update("jobs", {
            id: job.id,
            data: { index: job.index - 1 },
            previousData: job,
          }),
        ),
      // Increase indices in destination column
      ...destJobs
        .filter((job) => job.index >= destinationIndex)
        .map((job) =>
          dataProvider.update("jobs", {
            id: job.id,
            data: { index: job.index + 1 },
            previousData: job,
          }),
        ),
      // Update the moved job
      dataProvider.update("jobs", {
        id: source.id,
        data: {
          index: destinationIndex,
          status: destination.status,
        },
        previousData: source,
      }),
    ]);
  }
};
